import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adminFetch } from '@/lib/apiClient';
import { throwFromResponse } from '@/features/users/lib/apiErrorMessage';

/**
 * C-1 (ID103) 카테고리 전시 유형 — 카테고리가 어느 SF 서브탭(EVER MUSIC/MD/CON/FRIENDS 등)에
 * 속하는지 분류한다. null = 미분류(서브탭 무소속). BE `CategoryDisplayType`(categories.display_type, V96)와 1:1.
 * ⚠ 상품 라이프사이클 유형(ProductType: GENERAL/PRESALE/…)과는 별개 축이다.
 */
export type CategoryDisplayType = 'MUSIC' | 'MD' | 'CON' | 'FRIENDS' | 'LIGHT_STICK' | 'PRE_ORDER';

export interface AdminCategoryView {
  categoryId: string;
  parentCategoryId: string | null;
  categoryCode: string;
  name: string;
  // C-1(ID103) 전시 유형. SF 서브탭 렌더 기준. null = 미분류. BE AdminCategoryView.displayType.
  displayType?: CategoryDisplayType | null;
  // C2 계약(be-o) — 표시용 라벨 SSoT. raw enum 직접노출 금지, 화면 표기는 이 값을 우선 사용.
  // 예: "EVER MUSIC"/"EVER MD"/…/"EVER LIGHT STICK"/"예약판매". 미공급(codegen lag) 시 FE 폴백 맵.
  displayTypeLabel?: string | null;
  // 이슈1(B2B 전용 카테고리, be-cat 계약) — 이 카테고리(+하위·소속 상품)를 B2B(기업) 회원에게만
  // 노출할지 여부. true = B2B 전용. BE categories.b2b_only. 미공급(구버전 응답) 시 false 로 취급.
  b2bOnly?: boolean;
  depth: number;
  sortOrder: number;
  slug: string;
  active: boolean;
  createdAt: string;
  updatedAt: string;
  children: AdminCategoryView[];
}

interface AdminCategoryEnvelope {
  success: boolean;
  data?: AdminCategoryView[] | null;
}

interface SingleCategoryEnvelope {
  success: boolean;
  data?: AdminCategoryView | null;
}

const CATEGORIES_QUERY_KEY = ['admin', 'categories'] as const;

const fetchAdminCategories = async (): Promise<AdminCategoryView[]> => {
  const res = await adminFetch('/admin/v1/categories');
  if (!res.ok) throw new Error(`categories fetch failed: ${res.status}`);
  const env = (await res.json()) as AdminCategoryEnvelope;
  return env.data ?? [];
};

export const useAdminCategoriesQuery = () =>
  useQuery<AdminCategoryView[]>({
    queryKey: CATEGORIES_QUERY_KEY,
    queryFn: fetchAdminCategories,
    staleTime: 60 * 1000,
  });

export interface CategoryCreatePayload {
  name: string;
  categoryCode: string;
  parentCategoryId?: string | null;
  // C-1(ID103) 전시 유형. null/생략 = 미분류.
  displayType?: CategoryDisplayType | null;
  // 이슈1 — B2B 전용 노출 여부. 생략 = BE 기본값(false).
  b2bOnly?: boolean;
  sortOrder?: number;
  slug?: string;
}

export interface CategoryUpdatePayload {
  name: string;
  categoryCode: string;
  // C-1(ID103) 전시 유형. null/생략 = 미분류.
  displayType?: CategoryDisplayType | null;
  // 이슈1 — B2B 전용 노출 여부. 생략 = BE 기본값(false).
  b2bOnly?: boolean;
  sortOrder?: number;
  slug?: string;
  active?: boolean;
}

export const useAdminCategoryMutation = () => {
  const qc = useQueryClient();
  const invalidate = () => qc.invalidateQueries({ queryKey: CATEGORIES_QUERY_KEY });

  const create = useMutation({
    mutationFn: async (payload: CategoryCreatePayload) => {
      const res = await adminFetch('/admin/v1/categories', {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      if (!res.ok) await throwFromResponse(res, '카테고리 등록에 실패했습니다.');
      const env = (await res.json()) as SingleCategoryEnvelope;
      return env.data!;
    },
    onSuccess: invalidate,
  });

  const update = useMutation({
    mutationFn: async ({ categoryId, payload }: { categoryId: string; payload: CategoryUpdatePayload }) => {
      const res = await adminFetch(`/admin/v1/categories/${categoryId}`, {
        method: 'PUT',
        body: JSON.stringify(payload),
      });
      if (!res.ok) await throwFromResponse(res, '카테고리 수정에 실패했습니다.');
      const env = (await res.json()) as SingleCategoryEnvelope;
      return env.data!;
    },
    onSuccess: invalidate,
  });

  const remove = useMutation({
    mutationFn: async (categoryId: string) => {
      const res = await adminFetch(`/admin/v1/categories/${categoryId}`, {
        method: 'DELETE',
      });
      // BE 는 삭제를 막을 때 사유를 내려준다(예: 409 "상품이 연결된 카테고리는 삭제할 수 없습니다
      // (연결 상품 N개)"). status 만 던지면 그 사유가 사라져 화면이 아무 말도 하지 못한다(QA #213).
      if (!res.ok) await throwFromResponse(res, '카테고리 삭제에 실패했습니다.');
    },
    onSuccess: invalidate,
  });

  return { create, update, remove };
};
